import { motion } from "framer-motion";
import { PenTool, PhoneCall, Crown, ArrowUpRight, Check } from "lucide-react";

const PACKS = [
  {
    id: "contenu",
    icon: PenTool,
    name: "Giulia Contenu",
    desc: "Veille + scripts + calendrier + vidéos & visuels IA chaque mois.",
    bullets: [
      "Veille concurrentielle & tendances",
      "Scripts seconde par seconde",
      "Calendrier éditorial, validation en 1 clic",
      "Vidéos & visuels IA chaque mois",
    ],
    phare: false,
  },
  {
    id: "complet",
    icon: Crown,
    name: "Giulia Complète",
    desc: "Les 6 pôles réunis + un débrief mensuel avec un humain.",
    bullets: [
      "Les 6 pôles du cycle Giulia",
      "De la veille au rendez-vous signé",
      "Débrief mensuel avec un humain",
      "Un seul interlocuteur : Giulia",
    ],
    phare: true,
  },
  {
    id: "clients",
    icon: PhoneCall,
    name: "Giulia Clients",
    desc: "Capture + réponse 24/7 + RDV + relances + réputation.",
    bullets: [
      "Réponse instantanée aux leads",
      "Agent vocal & chatbot 24/7",
      "Prise de RDV avec rappels",
      "Relances & avis Google automatiques",
    ],
    phare: false,
  },
];

const cardAnim = (i) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
});

export const PacksSection = ({ onSelectPack }) => (
  <section id="packs" className="relative py-24 sm:py-32 scroll-mt-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-viol-soft mb-4">02 — Les packs</p>
        <h2 className="font-display font-bold text-3xl sm:text-5xl text-white leading-tight">
          Trois façons d'installer Giulia.
        </h2>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
        {PACKS.map((p, i) => {
          const Icon = p.icon;
          const card = (
            <article
              data-testid={`pack-card-${p.id}`}
              className={`relative h-full flex flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                p.phare
                  ? "bg-ink-surface shadow-[0_0_48px_rgba(139,92,246,0.25)]"
                  : "bg-ink-surface/60 border border-ink-line"
              }`}
            >
              {p.phare && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-iris-deep to-viol px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white whitespace-nowrap" data-testid="pack-badge-phare">
                  Pack phare
                </span>
              )}
              <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border ${p.phare ? "bg-viol/15 border-viol/40 text-viol-pale" : "bg-viol/10 border-viol/25 text-viol-soft"}`}>
                <Icon size={20} />
              </span>
              <h3 className="mt-5 font-display font-bold text-2xl text-white">{p.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-mist">{p.desc}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-white/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-viol-soft" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pack-cta-${p.id}`}
                onClick={() => onSelectPack(p.name)}
                className={`mt-8 group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 ${
                  p.phare
                    ? "bg-gradient-to-r from-iris-deep to-viol text-white shadow-[0_0_28px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)]"
                    : "border border-viol/35 text-viol-pale hover:bg-viol/10 hover:border-viol/60"
                }`}
              >
                Demander une démo
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </article>
          );

          return (
            <motion.div key={p.id} {...cardAnim(i)} className={p.phare ? "md:-mt-6 md:mb-[-1.5rem]" : ""}>
              {p.phare ? (
                <div className="h-full rounded-2xl p-[1.5px] bg-gradient-to-b from-viol via-iris to-viol/40">{card}</div>
              ) : (
                card
              )}
            </motion.div>
          );
        })}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mt-12 text-center font-mono text-sm text-mist"
        data-testid="packs-pricing-mention"
      >
        Installation + abonnement mensuel, sur devis. Rien à la pièce.
      </motion.p>
    </div>
  </section>
);

export default PacksSection;
